import { useState, useRef, useEffect } from 'react';

interface PromptInputProps {
  onSubmit: (prompt: string) => void;
  disabled?: boolean;
}

export default function PromptInput({ onSubmit, disabled }: PromptInputProps) {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    console.log('[PromptInput] handleSubmit called, value:', trimmed, 'disabled:', disabled);
    if (trimmed && !disabled) {
      console.log('[PromptInput] Calling onSubmit...');
      onSubmit(trimmed);
      setValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className={`relative bg-white border transition-all duration-150 ${
        isFocused
          ? 'border-mint shadow-subtle'
          : 'border-grey-medium/30'
      }`}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder="Ask about your data..."
        disabled={disabled}
        rows={1}
        className="w-full px-3 md:px-4 py-3 pr-12 bg-transparent border-0 resize-none focus:outline-none text-sm md:text-base text-black font-light placeholder-grey-medium disabled:opacity-50"
      />
      <button
        onClick={handleSubmit}
        disabled={disabled || !value.trim()}
        className="absolute right-2 bottom-2 p-2 bg-mint text-black rounded-full hover:bg-mint/90 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-mint transition-all"
        aria-label="Send message"
      >
        {disabled ? (
          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
            />
          </svg>
        )}
      </button>

      {/* Keyboard hint */}
      <div className="absolute left-3 md:left-4 bottom-2 hidden md:block">
        <span className="text-xs text-grey-medium/60 font-mono uppercase tracking-wider">
          Enter to send
        </span>
      </div>
    </div>
  );
}
